/**
 * The QC **kind** rules for the Test Code form — ported from the prototype's
 * `QK` / `QF` / `qcKindOf` (Test-Code-v12 lines 499, 623-636).
 *
 * A QC type's *kind* is NOT its raw role: **Spike and Standard are one kind**,
 * because they are checked against the same limits. That single fact drives
 * three things on this form, which is why it lives in one place rather than
 * being re-derived per tab:
 *
 *  - which limit columns a QC type's grid shows (`cols`),
 *  - which of them must be filled for the type to be "complete" (`req`),
 *  - the pass rule shown beside the QC type (`rule`).
 *
 * Consumed by `TestCodeDetailTabs` (QC Limits / QC Parameter Defaults) and by
 * `TestQcCopyDialogs` (which QC types may receive another's limits).
 */

export type QcKind = "blank" | "dup" | "spike" | "spdup";

/** A QC parameter row as `/qc-parameters` returns it. Only the fields these
 *  rules read are named; everything else travels untouched. */
export type QcParamRow = {
  oid?: string;
  qcTypeId?: string | null;
  qcType?: string | null;
  qcRole?: string | null;
  qcRootRole?: string | null;
  parameterId?: string | null;
  parameter?: string | null;
  retireDate?: string | null;
  [k: string]: unknown;
};

/** Column keys → the row field each one reads, and its header. The headers are
 *  the prototype's `QF` map verbatim, so a column reads the same here as it
 *  does there and in the legacy grid. */
export const QC_FIELD: Record<string, { field: string; header: string; num?: boolean }> = {
  rl:     { field: "rl", header: "RL", num: true },
  mdl:    { field: "mdl", header: "MDL", num: true },
  du:     { field: "defaultUnits", header: "Units" },
  ucl:    { field: "ucLimit", header: "UCLimit", num: true },
  rpd:    { field: "rpdCtrlLimit", header: "RPDCtrlLimit", num: true },
  spk:    { field: "spikeAmount", header: "SpikeAmount", num: true },
  spku:   { field: "spikeUnits", header: "SpikeUnits" },
  reclcl: { field: "recLcLimit", header: "RecLCLimit", num: true },
  recucl: { field: "recUcLimit", header: "RecUCLimit", num: true },
  rpdsf:  { field: "rpdSigFig", header: "RPDSigFig", num: true },
  rpdco:  { field: "rpdCutOff", header: "RPDCutOff", num: true },
  rpddec: { field: "rpdDecimal", header: "RPDDecimal", num: true },
  recsf:  { field: "recSigFig", header: "RECSigFig", num: true },
  recco:  { field: "recCutOff", header: "RECCutOff", num: true },
  recdec: { field: "recDecimal", header: "RECDecimal", num: true },
  sf:     { field: "sigFig", header: "SigFig", num: true },
  co:     { field: "cutOff", header: "CutOff", num: true },
  dec:    { field: "decimal", header: "Decimal", num: true },
  cmt:    { field: "comment", header: "Comment" },
};

/** The prototype's QK map (v12 line 624) — label, pass rule, required fields
 *  and the column set, per kind. */
export const QC_KIND: Record<QcKind, { label: string; rule: string; req: string[]; cols: string[] }> = {
  blank: {
    label: "Blank",
    rule: "Result < UCLimit",
    req: ["ucl"],
    cols: ["rl", "mdl", "du", "ucl", "sf", "co", "dec", "cmt"],
  },
  dup: {
    label: "Duplicate",
    rule: "RPD ≤ RPDCtrlLimit",
    req: ["rpd"],
    cols: ["rl", "mdl", "du", "rpd", "rpdsf", "rpdco", "rpddec", "sf", "co", "dec", "cmt"],
  },
  spike: {
    label: "Spike / Standard",
    rule: "RecLCLimit ≤ %Rec ≤ RecUCLimit",
    req: ["spk", "reclcl", "recucl"],
    cols: ["du", "spk", "spku", "reclcl", "recucl", "recsf", "recco", "recdec", "sf", "co", "dec", "cmt"],
  },
  spdup: {
    label: "Spike duplicate",
    rule: "%Rec within limits and RPD ≤ RPDCtrlLimit",
    req: ["spk", "reclcl", "recucl", "rpd"],
    cols: ["du", "spk", "spku", "reclcl", "recucl", "rpd", "recsf", "recco", "recdec",
           "rpdsf", "rpdco", "rpddec", "sf", "co", "dec", "cmt"],
  },
};

/** Prototype `qcKindOf` (v12 line 499). ⚠ Spike and Standard both land on
 *  "spike" — that is the point of a kind. */
export function qcKindOf(r: { qcRole?: string | null; qcRootRole?: string | null }): QcKind {
  if (r.qcRootRole === "Blank") return "blank";
  if (r.qcRole === "Duplicate") return r.qcRootRole === "Duplicate" ? "dup" : "spdup";
  return "spike";
}

/** Prototype `effVal` (v12 line 635): a row's own value, else the one inherited
 *  from its sample parameter. The API resolves the inherited values, so this
 *  only has to prefer one over the other. */
export function effValue(row: QcParamRow, key: string): string {
  const f = QC_FIELD[key];
  if (!f) return "";
  const own = row[f.field];
  if (own !== null && own !== undefined && String(own).trim() !== "") return String(own);
  const inh = row[`${f.field}Inherited`];
  return inh === null || inh === undefined ? "" : String(inh).trim();
}

/** Is a required field filled (own value or inherited)? */
export const hasValue = (row: QcParamRow, key: string) => effValue(row, key) !== "";

/** How many required limit values a QC type is still missing, across its rows
 *  (prototype `missingIn`, v12 line 636). Rows of a RETIRED sample parameter
 *  are not counted — the lab stopped reporting them. */
/** Retired = a RetireDate that has been reached. A future date only SCHEDULES it,
 *  so until then the row is live (prototype v12 isRet; AI_TestMethod_CopySettings_SP
 *  reads it the same way). Compared as yyyy-mm-dd, the stored wall-clock date. */
export function isRetired(r: { retireDate?: unknown } | null | undefined): boolean {
  const v = r?.retireDate;
  if (!v) return false;
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return String(v).slice(0, 10) <= today;
}

export function missingIn(
  rows: QcParamRow[],
  kind: QcKind,
  isRetiredParam: (r: QcParamRow) => boolean = () => false,
): number {
  const req = QC_KIND[kind].req;
  return rows
    .filter((r) => !isRetiredParam(r))
    .reduce((a, r) => a + req.filter((f) => !hasValue(r, f)).length, 0);
}

/** Does this kind use that column at all? The prototype renders an em-dash in
 *  a cell the role never uses, rather than leaving it blank as if it were
 *  simply unfilled (v12 QC Parameter Defaults legend). */
export const kindUses = (kind: QcKind, key: string) => QC_KIND[kind].cols.includes(key);
